import MembershipService from '@xo-union/sdk-membership';
import { conversationsMiddleware } from '@xo-union/store-conversations-redux';
import * as membershipRedux from '@xo-union/store-membership-redux';
import { identifyMemberMiddleware } from '@xo-union/tk-analytics-redux';
import { applyMiddleware, compose, createStore } from 'redux';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import api from './middleware/api';
import reducers from './redux';

const membershipService = new MembershipService({
	clientOptions: {
		env: process.env.NODE_ENV === 'production' ? 'production' : 'qa',
		apiKey: process.env.MEMBERSHIP_API_KEY,
		includeWeddingProperties: true,
	},
});

/** @knipignore */
export const setSessionStorage = () => (next) => (action) => {
	if (action.type === 'middleware/SET_SESSION_STORAGE') {
		if (window?.sessionStorage) {
			window.sessionStorage.setItem(action.payload.key, action.payload.value);
			window.dispatchEvent(
				new StorageEvent(`sessionStorage-${action.payload.key}`, {
					key: action.payload.key,
					newValue: action.payload.value,
				}),
			);
		}
	}
	return next(action);
};

export const LAST_USED_EMAIL_KEY = 'app:TKMarketplace:lastUsedEmail';

/** @knipignore */
export const trackLastUsedEmailMiddleware = (store) => (next) => (action) => {
	const stateBefore = store.getState();
	const result = next(action);

	if (
		[membershipRedux.LOG_IN_SUCCESS, membershipRedux.SIGN_UP_SUCCESS].includes(
			action.type,
		)
	) {
		const stateAfter = store.getState();

		try {
			// Only run in browser
			if (typeof window === 'undefined' || !window.localStorage) {
				return result;
			}

			const emailBefore = stateBefore.membership?.member?.email;
			const emailAfter = stateAfter.membership?.member?.email;

			// Determine the "current" email (prefer after, fallback to before)
			const currentEmail = emailAfter || emailBefore;

			// Only update if:
			// 1. We have a non-null email
			// 2. It's different from what's stored
			if (currentEmail) {
				const storedEmail = window.localStorage.getItem(LAST_USED_EMAIL_KEY);
				if (currentEmail !== storedEmail) {
					window.localStorage.setItem(LAST_USED_EMAIL_KEY, currentEmail);
				}
			}
		} catch (error) {
			console.warn('Failed to track last used email:', error.message);
		}
	}

	return result;
};

/* eslint-disable-next-line no-new-func */
const isNode = new Function(
	'try {return this===global;}catch(e){return false;}',
);

const loggerMiddleware =
	isNode() ||
	process.env.NODE_ENV === 'production' ||
	process.env.NODE_ENV === 'test'
		? []
		: [createLogger({ collapsed: true, diff: true })];

const conversationMiddleware = isNode() ? [] : [conversationsMiddleware()];

const trackLastUsedEmail = isNode() ? [] : [trackLastUsedEmailMiddleware];

/* eslint-disable no-mixed-operators, no-underscore-dangle */
const composeEnhancers =
	(typeof window !== 'undefined' &&
		window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
	compose;

export default function (initialState = {}) {
	return createStore(
		reducers,
		initialState,
		composeEnhancers(
			applyMiddleware(
				// ordering matters
				identifyMemberMiddleware(),
				...conversationMiddleware,
				thunk.withExtraArgument({ membershipService }),
				api,
				setSessionStorage,
				...trackLastUsedEmail,
				...loggerMiddleware,
			),
		),
	);
}
